import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import client from "@/lib/api";
import { PrimaryBadge } from "@/components/StatusBadge";
import { fmtDate, dueLabel, isOverdue, timeAgo } from "@/lib/format";
import { ArrowRight } from "lucide-react";
const SUMMARY_CARDS = [
  { key: "active_jobs", label: "Active Jobs", to: "/app/board?filter=active" },
  { key: "needs_attention", label: "Needs Attention", to: "/app/needs-attention" },
  { key: "missing_documents", label: "Missing Documents", to: "/app/documents?status=Missing" },
  { key: "customer_updates_due", label: "Customer Updates Due", to: "/app/customer-updates?filter=overdue" },
  { key: "pickups_today", label: "Pickups Today", to: "/app/board?filter=pickup-today" },
  { key: "deliveries_today", label: "Deliveries Today", to: "/app/board?filter=delivery-today" },
  { key: "ready_to_close", label: "Ready to Close", to: "/app/board?filter=ready-to-close" },
  { key: "closeout_blocked", label: "Closeout Blocked", to: "/app/board?filter=closeout-blocked" },
  { key: "closed", label: "Closed", to: "/app/board?filter=closed" },
];

// Caribbean / inter-island maritime accounts use a streamlined 4-card delivery grid.
const MARITIME_CARDS = [
  { key: "active_jobs", label: "Active Jobs", to: "/app/board?filter=active" },
  { key: "needs_attention", label: "Needs Attention", to: "/app/needs-attention" },
  { key: "awaiting_verification", label: "Awaiting Verification", to: "/app/documents?status=Missing" },
  { key: "completed_deliveries", label: "Completed Deliveries", to: "/app/board?filter=closed" },
];

function SectionTable({ title, label, jobs, columns, onRow, emptyText }) {
  return (
    <div className="bg-surface border border-line rounded-md overflow-hidden">
      <div className="px-5 py-3.5 border-b border-line flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <h3 className="font-heading text-[15px] font-semibold text-ink">{title}</h3>
          <span className="op-label" style={{ color: "#7B8794" }}>{label}</span>
        </div>
        <span className="font-mono-data text-xs text-ink-3">{jobs.length}</span>
      </div>
      {jobs.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-ink-3">{emptyText}</div>
      ) : (
        <div className="divide-y divide-line">
          {jobs.map((j) => (
            <button
              key={j.id}
              data-testid={`today-job-${j.job_number}`}
              onClick={() => onRow(j)}
              className="w-full text-left px-5 py-3 hover:bg-surface-alt transition-colors flex items-center gap-4 group focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-steel/40"
            >
              <div className="w-24 shrink-0">
                <div className="font-mono-data text-[12.5px] text-ink font-medium">{j.job_number}</div>
                <div className="text-[11px] text-ink-3 truncate">{j.assigned_owner.split(" ")[0]}</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13.5px] text-ink font-medium truncate">{j.customer_name}</div>
                <div className="text-[12px] text-ink-2 truncate">{columns(j)}</div>
              </div>
              <div className="shrink-0 text-right hidden sm:block">
                <PrimaryBadge job={j} />
              </div>
              <ArrowRight className="h-4 w-4 text-ink-3 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Today() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    client.get("/dashboard/today").then((r) => setData(r.data));
  }, []);

  const go = (j) => navigate(`/app/shipments/${j.id}`);

  if (!data)
    return (
      <Layout>
        <div className="text-sm text-ink-3 font-mono-data py-20 text-center">Loading dashboard…</div>
      </Layout>
    );

  const maritime = !!data.maritime;

  return (
    <Layout>
      <div className="mb-6">
        <p className="op-label mb-1">Daily control</p>
        <h1 className="font-heading text-2xl font-semibold text-ink">Today</h1>
        <p className="text-sm text-ink-2 mt-1">Where your team should start. What's moving, what's stuck, and what needs action next.</p>
      </div>

      {maritime ? (
        /* Maritime: 4-card heavy-bordered industrial grid */
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-7" data-testid="maritime-summary-grid">
          {MARITIME_CARDS.map((c) => (
            <button
              key={c.key}
              data-testid={`summary-${c.key}`}
              onClick={() => navigate(c.to)}
              className="text-left bg-white border-2 border-[#0B132B] rounded-md p-4 min-h-[104px] flex flex-col justify-between shadow-[2px_2px_0px_0px_#0B132B] transition-all hover:shadow-none hover:translate-x-[1px] hover:translate-y-[1px] focus:outline-none"
            >
              <div className="op-label leading-tight text-[#0B132B] font-bold">{c.label}</div>
              <div className="font-mono-data text-[30px] font-black text-[#0B132B] leading-none tracking-tight mt-3">{data.summary[c.key]}</div>
            </button>
          ))}
        </div>
      ) : (
        /* Overland: full continental counter grid */
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-9 gap-3 mb-7">
          {SUMMARY_CARDS.map((c) => (
            <button
              key={c.key}
              data-testid={`summary-${c.key}`}
              onClick={() => navigate(c.to)}
              className="text-left bg-surface border border-line-strong border-l-4 border-l-brand rounded-sm p-3.5 min-h-[92px] flex flex-col justify-between shadow-industrialCard transition-shadow duration-150 hover:shadow-industrialHover focus:outline-none focus-visible:ring-2 focus-visible:ring-warn"
            >
              <div className="op-label leading-tight">{c.label}</div>
              <div className="font-mono-data text-[26px] font-semibold text-ink leading-none tracking-tight mt-3">{data.summary[c.key]}</div>
            </button>
          ))}
        </div>
      )}

      {/* Sections */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="lg:col-span-2">
          <SectionTable
            title="Priority Jobs"
            label="Needs Attention"
            jobs={data.priority_jobs}
            onRow={go}
            emptyText="No priority jobs right now."
            columns={(j) => j.issues.join(" · ") || j.next_action}
          />
        </div>
        <SectionTable
          title="Overdue Next Actions"
          label="Past Due"
          jobs={data.overdue_next_actions}
          onRow={go}
          emptyText="No overdue next actions."
          columns={(j) => `${j.next_action} — ${dueLabel(j.next_action_due_date)}`}
        />
        {maritime ? (
          <SectionTable
            title="Awaiting Delivery Verification"
            label="Proof Pending"
            jobs={data.awaiting_verification || []}
            onRow={go}
            emptyText="All delivered drop-offs are verified."
            columns={(j) => `${j.delivery_location} · Delivered — proof photo pending`}
          />
        ) : (
          <>
            <SectionTable
              title="Missing Documents"
              label="Missing Documents"
              jobs={data.missing_documents}
              onRow={go}
              emptyText="No missing documents."
              columns={(j) => (j.documents.filter((d) => d.status === "Missing").map((d) => d.type).join(", ") || "—")}
            />
            <SectionTable
              title="Customer Updates Due Today"
              label="Customer Update Due"
              jobs={data.customer_updates_due}
              onRow={go}
              emptyText="No customer updates due."
              columns={(j) => `${j.current_stage} · last update ${timeAgo(j.last_updated_at)}`}
            />
            <SectionTable
              title="Jobs Ready to Close"
              label="Ready to Close"
              jobs={data.ready_to_close}
              onRow={go}
              emptyText="No jobs ready to close."
              columns={(j) => `${j.delivery_location} · delivered ${fmtDate(j.delivery_due_date)}`}
            />
          </>
        )}
      </div>
    </Layout>
  );
}
